import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { WppTableBodyRow, WppTableBody, WppTableBodyCell, WppInlineMessage, WppTableHeaderCell, WppTableHeaderRow, WppTableHeader, WppTable } from 'buildingBlocks';
import { Metric, displayMetrics } from 'containers/StrategyAnalytics/constants/metricsConstants';
import { LeafAnalyticsPerFlight } from 'containers/StrategyAnalytics/types';
import { tableStyle } from 'containers/StrategyAnalytics/styles';
import { SEGMENT_AGE } from '../constants';

type Props = {
  data: LeafAnalyticsPerFlight
  noData: boolean
  metrics: Array<Metric>
  loading: boolean
  filteredName?: string | null
  filteredData?: LeafAnalyticsPerFlight
};

type TableHeaderProps = {
  headers: Array<Metric>
};

const getDefaultSegmentAgeTable = () => _.reduce(SEGMENT_AGE, (res, { id }) => {
  res[id] = [];
  return res;
}, {});

const SEGMENT_AGE_INTERVAL_NAME = 'segmentAgeInterval';
const SEGMENT_AGE_INTERVAL_COL = { text: 'Segment Age Interval', value: SEGMENT_AGE_INTERVAL_NAME, func: () => 0 };

type SegmentAgeCellProps = {
  id: string
  value: Array<string>
};

const TableHeader: React.FC<TableHeaderProps> = ({ headers }: TableHeaderProps): React.ReactElement => (
  <WppTableHeader>
    <WppTableHeaderRow>
      {(_.map(headers, (header) => (
        <WppTableHeaderCell
          key={header.value}
          textAlign="center"
          title={header.text}
          colSpan={_.isEqual(header.value, SEGMENT_AGE_INTERVAL_NAME) ? 2 : undefined}
        />
      )))}
    </WppTableHeaderRow>
  </WppTableHeader>
);

const SegmentAgeCell: React.FC<SegmentAgeCellProps> = ({ id, value }) => (
  <>
    {_.map(value, (segmentAge, index: number) => (
      <WppTableBodyCell
        key={`${id}-${index}`}
        style={tableStyle.content}
        textAlign={_.isEqual(index, 0) ? 'right' : 'left'}
        title={segmentAge}
      />
    ))}
  </>
);

const buildHeaders = (props: Props) => ({ headers: [SEGMENT_AGE_INTERVAL_COL, ...props.metrics] });

const buildTableData = (props: Props) => _.reduce(props.data, (res, row) => {
  const segmentAgeIntervalData = SEGMENT_AGE[row.segmentAgeInterval];
  if (segmentAgeIntervalData) {
    res[segmentAgeIntervalData.id].push(row);
  }
  return res;
}, getDefaultSegmentAgeTable());

const SegmentAgeTable: React.FC<Props> = (props) => {
  const [headers, setHeaders] = useState<Array<Metric>>([]);
  const [tableData, setTableData] = useState<Record<string, LeafAnalyticsPerFlight>>({});

  useEffect(() => {
    setHeaders(buildHeaders(props).headers);
    setTableData(buildTableData(props));
  }, [props]);

  if (props.loading) {
    return null;
  }

  return (
    <WppTable id="segment-age-analytics">
      <TableHeader headers={headers} />
      {
        props.noData
          ? (
            <WppTableBody>
              <WppTableBodyRow>
                <WppTableBodyCell colSpan={_.size(headers)}>
                  <WppInlineMessage size="m" type="warning" message="This table currently has no analytics data to display." />
                </WppTableBodyCell>
              </WppTableBodyRow>
            </WppTableBody>
          )
          : (
            <WppTableBody>
              {
                _.map(SEGMENT_AGE, (segmentAge) => (
                  <WppTableBodyRow key={segmentAge.id}>
                    {
                      _.map(headers, (header) => (_.isEqual(header.value, SEGMENT_AGE_INTERVAL_NAME)
                        ? <SegmentAgeCell key={`${segmentAge.id}-${header.value}`} {...segmentAge} />
                        : (
                          <WppTableBodyCell
                            key={`${segmentAge.id}-${header.value}`}
                            textAlign="center"
                            title={displayMetrics(header, header.func(_.get(tableData, segmentAge.id)))}
                          />
                        )))
                    }
                  </WppTableBodyRow>
                ))
              }
            </WppTableBody>
          )
      }
    </WppTable>
  );
};

export default SegmentAgeTable;
